import { checkPhoneNumber } from 'parent-portal/public/PhoneNumberExists';
import client from 'shared/ApiClient';
import { getUA } from 'react-device-detect';
import { track } from 'shared/Tracking';
import { consoleLogInDev } from 'shared';
import { useCookies } from 'react-cookie';
import useQueryParams from 'shared/QueryParamsHook';
import usePublicSessionTracking, {
    getPublicUserInfo,
    PublicSessionEventType,
} from '../parent-portal/shared/PublicSessionTracking';

type SignUpParameters = CompletionParameters & {
    email: string;
    password: string;
};

type CompletionParameters = {
    phoneNumber: string;
    shareSource: string;
    signupSource?: string;
    signupRoute?: string;
    secondaryRoute?: string;
    signUpForTandemHustle?: boolean;
    howDidYouHear?: { type: string; other: string };
    onUserCreated?: (token: string) => void;
};

type AllCompletionParameters = CompletionParameters & {
    referralCode?: string;
    source?: string;
};

export default function useProviderSignUp() {
    const [{ source, referral }] = useCookies(['source', 'referral']);
    const query = useQueryParams();
    const { trackSession } = usePublicSessionTracking('Provider Sign Up Api');

    async function completeSignUp({
        email,
        password,
        phoneNumber,
        shareSource,
        signupSource,
        signupRoute,
        secondaryRoute,
        signUpForTandemHustle,
        onUserCreated,
        howDidYouHear,
    }: SignUpParameters) {
        const { duplicate } = await checkPhoneNumber(phoneNumber);
        const referralCode = query.get('referral') ?? referral;

        if (!duplicate) {
            try {
                let user = {
                    username: email.toLocaleLowerCase(),
                    email: email.toLocaleLowerCase(),
                    password1: password,
                    password2: password,
                };

                const registrationResponse = await client('api/rest-auth/registration/', {
                    body: user,
                });

                const userResponse = await client('api/users/identity/', {
                    headers: { Authorization: 'Token ' + registrationResponse.key },
                });

                const userId = userResponse.id;
                await completeUserCreation(userId, registrationResponse.key, {
                    phoneNumber,
                    shareSource,
                    signupSource,
                    signupRoute,
                    secondaryRoute,
                    signUpForTandemHustle,
                    howDidYouHear,
                    referralCode,
                });

                onUserCreated && onUserCreated(registrationResponse.key);
            } catch (error) {
                consoleLogInDev(error);
                throw error;
            }
            if (referralCode) {
                postReferral(phoneNumber, referralCode, shareSource);
            }
        } else {
            throw new Error(`User with phone number ${phoneNumber} already exists`);
        }
    }

    async function completeUserCreation(
        userId: string,
        authToken: string,
        {
            phoneNumber,
            signUpForTandemHustle,
            signupRoute,
            signupSource,
            referralCode,
            howDidYouHear,
            secondaryRoute,
        }: AllCompletionParameters,
    ) {
        const campaignSource = query.get('source') ?? source;

        let form = {
            phone_number: phoneNumber,
            web_partial: true,
            user_agent: getUA ? getUA : null,
            hustle_interest: signUpForTandemHustle,
            signup_source: signupSource,
            signup_route: signupRoute,
            secondary_route: secondaryRoute,
            signup_referral_code: referralCode,
            campaign_source: campaignSource,
            discovery_source: howDidYouHear?.type,
            overall_tracking_code: referralCode ?? campaignSource,
            other_text: howDidYouHear?.type === 'Other' ? howDidYouHear.other : undefined,
            ...getPublicUserInfo(),
        };

        await client(`api/users/${userId}/`, {
            method: 'PATCH',
            headers: { Authorization: 'Token ' + authToken },
            body: form,
        });

        trackSession('SIGNUP', 'Provider Sign Up', PublicSessionEventType.User, {
            userId,
            signupRoute,
            phone_number: phoneNumber,
        });

        track('Provider Sign Up', { signupRoute });
    }

    const postReferral = (phoneNumber: string, referralCode: string, shareSource: string) => {
        client('api/sitter-referral-with-code/', {
            body: {
                referree_phone_number: phoneNumber,
                referral_code: referralCode,
                partial: true,
                share_source: shareSource,
            },
        });
    };

    return completeSignUp;
}
